/**
 * NodeKind-related API helpers.
 * Mirrors the Node and Synapse service structure for consistency.
 */
export const createNodeKindService = (apiClient) => {
  if (!apiClient) {
    throw new Error('apiClient instance is required to create NodeKind service');
  }

  return {
    /**
     * Create a new node kind with related entities.
     * @param {Object} nodeKindData
     * @param {Object} nodeKindData.nodeKind - Main node kind data
     * @param {string} nodeKindData.nodeKind.kindName - Kind name (required)
     * @param {string} [nodeKindData.nodeKind.description] - Description
     * @param {boolean} [nodeKindData.nodeKind.isNeuronal] - Is neuronal flag
     * @param {string} [nodeKindData.nodeKind.behaviorProfileTag] - Behavior profile tag
     * @param {string} [nodeKindData.nodeKind.lineagePath] - Lineage path
     * @param {number} [nodeKindData.nodeKind.lineageDepth] - Lineage depth
     * @param {Array} [nodeKindData.closure] - Closure entries
     * @param {Array} [nodeKindData.hierarchy] - Hierarchy entries
     * @param {Array} [nodeKindData.neuralProfiles] - Neural profile entries
     * @param {Array} [nodeKindData.rateTemplates] - Rate template entries
     * @returns {Promise<Object>} API response payload
     */
    createNodeKind: async (nodeKindData) => {
      if (!nodeKindData.nodeKind || !nodeKindData.nodeKind.kindName) {
        throw new Error('kindName is required to create node kind');
      }
      const response = await apiClient.post('/node-kinds', nodeKindData);
      return response.data;
    },

    /**
     * Retrieve a node kind by its ID.
     * @param {string} nodeKindId - Node kind identifier (required)
     * @returns {Promise<Object>} API response payload
     */
    getNodeKind: async (nodeKindId) => {
      if (!nodeKindId) {
        throw new Error('nodeKindId is required to fetch node kind details');
      }
      const response = await apiClient.get(`/node-kinds/${nodeKindId}`);
      return response.data;
    },

    /**
     * List all available node kinds.
     * @param {Object} [options] - Optional parameters
     * @param {number} [options.page=1] - Page number
     * @param {number} [options.pageSize=25] - Records per page
     * @returns {Promise<Object>} API response payload
     */
    listNodeKinds: async (options = {}) => {
      const params = {
        page: options.page || 1,
        pageSize: options.pageSize || 25,
      };

      if (options.query) {
        params.query = options.query;
      }

      const response = await apiClient.get('/node-kinds', { params });
      return response.data;
    },
  };
};

export default createNodeKindService;

